import type { CalendarEvent, EventInput, EventKind } from "@ronin/contracts";
import { addDays, diffDays, zonedParts, zonedToInstant } from "@ronin/core";

/** The event dialog's fields: local dates and times in the user's timezone, as strings. */
export interface EventFormValues {
  kind: EventKind;
  title: string;
  allDay: boolean;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  location: string;
  description: string;
  birthYear: string;
}

const MINUTE = 60_000;

/** A new event: all-day on `date`, or 1 hour from `time` (FR3, FR4). */
export function newEventValues(
  date: string,
  time: string | undefined,
  timeZone: string,
): EventFormValues {
  const base = {
    kind: "event" as const,
    title: "",
    location: "",
    description: "",
    birthYear: "",
  };
  if (!time) {
    return {
      ...base,
      allDay: true,
      startDate: date,
      startTime: "09:00",
      endDate: date,
      endTime: "10:00",
    };
  }
  const end = zonedParts(
    new Date(zonedToInstant(date, time, timeZone).getTime() + 60 * MINUTE),
    timeZone,
  );
  return {
    ...base,
    allDay: false,
    startDate: date,
    startTime: time,
    endDate: end.date,
    endTime: end.time,
  };
}

export function valuesFromEvent(event: CalendarEvent, timeZone: string): EventFormValues {
  const base = {
    kind: event.kind,
    title: event.title,
    location: event.location ?? "",
    description: event.description ?? "",
    birthYear: event.birthYear === null ? "" : String(event.birthYear),
  };
  if (event.allDay || !event.startsAt || !event.endsAt) {
    return {
      ...base,
      allDay: true,
      startDate: event.startDate ?? "",
      startTime: "09:00",
      endDate: event.endDate ?? event.startDate ?? "",
      endTime: "10:00",
    };
  }
  const start = zonedParts(new Date(event.startsAt), timeZone);
  const end = zonedParts(new Date(event.endsAt), timeZone);
  return {
    ...base,
    allDay: false,
    startDate: start.date,
    startTime: start.time,
    endDate: end.date,
    endTime: end.time,
  };
}

/**
 * Moving the start moves the end by the same amount (FR7): same number of days for
 * all-day events, same duration for timed ones.
 */
export function moveStart(
  values: EventFormValues,
  next: { startDate?: string; startTime?: string },
  timeZone: string,
): EventFormValues {
  const startDate = next.startDate ?? values.startDate;
  const startTime = next.startTime ?? values.startTime;
  if (!startDate || !values.startDate || !values.endDate) {
    return { ...values, startDate, startTime };
  }
  if (values.allDay || !startTime || !values.startTime || !values.endTime) {
    const days = Math.max(0, diffDays(values.startDate, values.endDate));
    return { ...values, startDate, startTime, endDate: addDays(startDate, days) };
  }
  const duration =
    zonedToInstant(values.endDate, values.endTime, timeZone).getTime() -
    zonedToInstant(values.startDate, values.startTime, timeZone).getTime();
  const end = zonedParts(
    new Date(zonedToInstant(startDate, startTime, timeZone).getTime() + Math.max(duration, 0)),
    timeZone,
  );
  return { ...values, startDate, startTime, endDate: end.date, endTime: end.time };
}

/** A message when the values cannot be saved, else null. */
export function formError(values: EventFormValues, timeZone: string): string | null {
  if (values.title.trim() === "") return "Add a title.";
  if (!values.startDate) return "Pick a date.";
  if (values.kind === "birthday") {
    const year = values.birthYear.trim();
    if (year && (!/^\d{4}$/.test(year) || Number(year) > Number(values.startDate.slice(0, 4)))) {
      return "The birth year must be a year up to the date.";
    }
    return null;
  }
  if (!values.endDate) return "Pick an end date.";
  if (values.allDay)
    return values.endDate < values.startDate ? "The end is before the start." : null;
  if (!values.startTime || !values.endTime) return "Pick start and end times.";
  const start = zonedToInstant(values.startDate, values.startTime, timeZone);
  const end = zonedToInstant(values.endDate, values.endTime, timeZone);
  return end <= start ? "The end is not after the start." : null;
}

/** Form values to API input; birthdays are one all-day date. */
export function toEventInput(values: EventFormValues, timeZone: string): EventInput {
  const common = {
    kind: values.kind,
    title: values.title.trim(),
    location: values.location.trim() || null,
    description: values.description.trim() || null,
  };
  if (values.kind === "birthday") {
    return {
      ...common,
      allDay: true,
      startDate: values.startDate,
      endDate: values.startDate,
      birthYear: values.birthYear.trim() ? Number(values.birthYear) : null,
    };
  }
  if (values.allDay) {
    return { ...common, allDay: true, startDate: values.startDate, endDate: values.endDate };
  }
  return {
    ...common,
    allDay: false,
    startsAt: zonedToInstant(values.startDate, values.startTime, timeZone).toISOString(),
    endsAt: zonedToInstant(values.endDate, values.endTime, timeZone).toISOString(),
  };
}
